import { TrendingUp, Info } from 'lucide-react';
import type { Profession, OverseasTerritory, StatusData, PrimesData } from '../../types';
import { formatCurrency } from '../../utils/formatting';

interface EstimationPreviewProps {
  profession: Profession;
  status: StatusData;
  territory: OverseasTerritory | null;
  gradeLabel: string | null;
  echelonLabel: string | null;
  primesData: PrimesData;
  brutMonthly?: number | null;
  netEstimatedMonthly?: number | null;
}

const STATUS_LABELS: Record<string, string> = {
  titulaire: 'Titulaire',
  stagiaire: 'Stagiaire',
  contractuel: 'Contractuel',
};

interface InfoRowProps {
  label: string;
  value: string;
}

function InfoRow({ label, value }: InfoRowProps) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <dt className="text-xs text-gray-500 flex-shrink-0">{label}</dt>
      <dd className="text-sm font-semibold text-gray-900 text-right truncate">{value}</dd>
    </div>
  );
}

export default function EstimationPreview({
  profession,
  status,
  territory,
  gradeLabel,
  echelonLabel,
  primesData,
  brutMonthly,
  netEstimatedMonthly,
}: EstimationPreviewProps) {
  const hasAnyData = Boolean(territory || gradeLabel || primesData.totalMensuel > 0);
  const hasFigures =
    (typeof brutMonthly === 'number' && brutMonthly > 0) ||
    (typeof netEstimatedMonthly === 'number' && netEstimatedMonthly > 0);

  const gradeEchelonCombined = [gradeLabel, echelonLabel].filter(Boolean).join(' - ');

  return (
    <div className="bg-white rounded-2xl shadow-sm p-4 lg:p-5 sticky top-4 self-start">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-4 h-4 text-emerald-600" />
        <h2 className="text-[11px] font-bold tracking-wider text-gray-600 uppercase">
          Aperçu de ton estimation
        </h2>
      </div>

      {hasFigures && (
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="rounded-xl bg-gray-50 border border-gray-100 px-3 py-2.5">
            <div className="text-[10px] font-semibold tracking-wider text-gray-500 uppercase mb-0.5">
              Brut / mois
            </div>
            <div className="text-base font-bold text-gray-900 tabular-nums">
              {typeof brutMonthly === 'number' && brutMonthly > 0 ? formatCurrency(brutMonthly) : '—'}
            </div>
          </div>
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2.5">
            <div className="text-[10px] font-semibold tracking-wider text-emerald-700 uppercase mb-0.5">
              Net estimé
            </div>
            <div className="text-base font-bold text-emerald-700 tabular-nums">
              {typeof netEstimatedMonthly === 'number' && netEstimatedMonthly > 0
                ? formatCurrency(netEstimatedMonthly)
                : '—'}
            </div>
          </div>
        </div>
      )}

      <div className="mb-1">
        <p className="text-[10px] font-semibold tracking-wider text-gray-400 uppercase">
          Détail de ta situation
        </p>
      </div>

      <dl className="divide-y divide-gray-100">
        <InfoRow label="Profession" value={profession.name} />
        <InfoRow label="Statut" value={STATUS_LABELS[status.type] ?? status.type} />
        {territory && <InfoRow label="Territoire" value={territory.name} />}
        {gradeEchelonCombined && <InfoRow label="Grade" value={gradeEchelonCombined} />}
        {primesData.totalMensuel > 0 && (
          <InfoRow label="Primes / mois" value={formatCurrency(primesData.totalMensuel)} />
        )}
      </dl>

      {!hasAnyData && (
        <div className="mt-4 p-3 rounded-lg bg-gray-50 flex items-start gap-2 text-xs text-gray-600">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-400" />
          <p>Renseigne ton activité pour découvrir ton estimation en temps réel.</p>
        </div>
      )}
    </div>
  );
}
